import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type BaseFilterCardProps, FilterCard } from '~/components/Filters/FilterCard';
import { TagsFilter, type TagsFilterProps } from '~/components/Filters/TagsFilter/TagsFilter';
import { type TagBase } from '~/components/Tags/utils';
import {
  clearTagsFilterValuesAction,
  removeFilterAction,
  setTagsFilterLogicAction,
  setTagsFiltersValueAction,
} from '~/store/filters/actions';
import { selectedTagsFilterLogicSelector, selectedTagsFiltersSelector } from '~/store/filters/selectors';
import { type FilterLogicType, type SelectedFilter } from '~/store/filters/types';

export type TagsFilterCardProps = {
  TagsSelect: TagsFilterProps['TagsSelect'];
} & BaseFilterCardProps;

export const TagsFilterCard = memo(({ TagsSelect, ...props }: TagsFilterCardProps) => {
  const dispatch = useDispatch();

  const clearValues = useCallback(() => {
    dispatch(clearTagsFilterValuesAction());
  }, [dispatch]);

  const selectedLogic = useSelector(selectedTagsFilterLogicSelector);
  const selectedTags = useSelector(selectedTagsFiltersSelector);

  const onLogicChange = useCallback(
    (logic: FilterLogicType) => dispatch(setTagsFilterLogicAction({ logic })),
    [dispatch],
  );

  const onSelectedTagsChanged = useCallback(
    (tags: TagBase[]) => dispatch(setTagsFiltersValueAction({ tags })),
    [dispatch],
  );

  const onRemoveFilter = (filter: SelectedFilter) => {
    dispatch(removeFilterAction({ filter }));
    clearValues();
  };

  return (
    <FilterCard currentFilter="tags" onClearClick={clearValues} onRemoveFilter={onRemoveFilter} {...props}>
      <TagsFilter
        TagsSelect={TagsSelect}
        selectedLogic={selectedLogic}
        selectedTags={selectedTags}
        onLogicChange={onLogicChange}
        onSelectedTagsChanged={onSelectedTagsChanged}
      />
    </FilterCard>
  );
});

TagsFilterCard.displayName = 'TagsFilterCard';
